import React, { useState, useEffect } from 'react';
import { api } from '../../api.js';
import AddressAutocomplete from '../../components/AddressAutocomplete.jsx';
import RelatedLoadsTab from './RelatedLoadsTab.jsx';
import DriverDocumentsTab from './DriverDocumentsTab.jsx';
import PhoneInput from '../../components/PhoneInput.jsx';

const blank = {
  firstName: '', lastName: '', dateOfBirth: '', placeOfBirth: '', citizenship: '',
  address: '', city: '', province: '', postalCode: '', country: 'Canada',
  email: '', phone: '', cell: '', emergencyContact: '', emergencyContactPhone: '',
  dispatchStatus: 'Active', driverStatus: 'Active', dateOfHiring: '',
  operationalStatus: '', driverType: '', jobStatus: '', division: '', driverPayOption: ''
};

function fromDriver(d) {
  if (!d) return blank;
  return {
    firstName: d.first_name || '', lastName: d.last_name || '', dateOfBirth: d.date_of_birth || '',
    placeOfBirth: d.place_of_birth || '', citizenship: d.citizenship || '',
    address: d.address || '', city: d.city || '', province: d.province || '',
    postalCode: d.postal_code || '', country: d.country || 'Canada',
    email: d.email || '', phone: d.phone || '', cell: d.cell || '',
    emergencyContact: d.emergency_contact || '', emergencyContactPhone: d.emergency_contact_phone || '',
    dispatchStatus: d.dispatch_status || 'Active', driverStatus: d.driver_status || 'Active',
    dateOfHiring: d.date_of_hiring || '',
    operationalStatus: d.operational_status || '', driverType: d.driver_type || '',
    jobStatus: d.job_status || '', division: d.division || '', driverPayOption: d.driver_pay_option || ''
  };
}

function timeWithUs(dateOfHiring) {
  if (!dateOfHiring) return '—';
  const start = new Date(dateOfHiring);
  const now = new Date();
  if (isNaN(start.getTime())) return '—';
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 0) return '—';
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  if (years === 0) return `${remMonths} mo`;
  return remMonths === 0 ? `${years} yr` : `${years} yr ${remMonths} mo`;
}

// Generic Date + Description add/list, used by Documents Info sub-tabs and
// Safety Record — no distinct field spec was given for these, so this is a
// simple starter pattern rather than category-specific fields.
function EntryLog({ entries, onAdd, onDelete, dateLabel = 'Date', descLabel = 'Description' }) {
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const add = () => {
    if (!description.trim()) return;
    onAdd({ date, description });
    setDate(''); setDescription('');
  };
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr auto', gap: 10, marginBottom: 14 }}>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} />
        <input placeholder={descLabel} value={description} onChange={e => setDescription(e.target.value)} />
        <button type="button" className="primary-btn" onClick={add}>+ Add</button>
      </div>
      {entries.length === 0
        ? <div className="empty">No entries yet.</div>
        : (
          <table>
            <thead><tr><th>{dateLabel}</th><th>{descLabel}</th><th></th></tr></thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id}>
                  <td>{e.date || e.entry_date || e.record_date || '—'}</td>
                  <td>{e.description}</td>
                  <td>
                    <button type="button" onClick={() => onDelete(e.id)} style={{ color: 'var(--red)' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  );
}

const TABS = [
  'Basic Information', 'Documents Info', 'Document Checklist', 'Driver Rates',
  'Related Documents', 'Driver Related Loads', 'Safety Records'
];

export default function DriverFormModal({ driver, onClose, onSaved }) {
  const [savedId, setSavedId] = useState(driver?.id || null);
  const [form, setForm] = useState(() => fromDriver(driver));
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [manualAddress, setManualAddress] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginInfo, setLoginInfo] = useState(null);
  const [existingLogin, setExistingLogin] = useState(undefined); // undefined = not checked yet, null = no login
  const [loginError, setLoginError] = useState(null);
  const [creatingLogin, setCreatingLogin] = useState(false);
  const [togglingLogin, setTogglingLogin] = useState(false);

  const checkExistingLogin = () => {
    if (!savedId) return;
    api(`/dispatch/drivers/${savedId}/login`).then(setExistingLogin).catch(() => setExistingLogin(null));
  };
  useEffect(() => { checkExistingLogin(); }, [savedId]);

  const createLogin = async () => {
    if (!loginEmail.trim()) { setLoginError('Enter an email first.'); return; }
    setCreatingLogin(true);
    setLoginError(null);
    try {
      const result = await api(`/dispatch/drivers/${savedId}/create-login`, { method: 'POST', body: { email: loginEmail } });
      setLoginInfo(result);
      checkExistingLogin();
    } catch (e) {
      setLoginError(e.message);
    } finally {
      setCreatingLogin(false);
    }
  };

  const toggleLogin = async () => {
    setTogglingLogin(true);
    setLoginError(null);
    try {
      const updated = await api(`/dispatch/drivers/${savedId}/login`, { method: 'PATCH', body: { isActive: !existingLogin.is_active } });
      setExistingLogin(updated);
    } catch (e) {
      setLoginError(e.message);
    } finally {
      setTogglingLogin(false);
    }
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // ── Document Checklist (tab 2) ──────────────────────────────────────────
  const [checklist, setChecklist] = useState(null);

  const loadChecklist = () => {
    if (!savedId) return;
    api(`/dispatch/drivers/${savedId}/checklist`).then(setChecklist).catch(() => setChecklist([]));
  };
  useEffect(() => { if (tab === 2 && savedId) loadChecklist(); }, [tab, savedId]);

  const toggleChecklistItem = async (item, isChecked) => {
    await api(`/dispatch/drivers/${savedId}/checklist/${item.doc_type_id}`, {
      method: 'POST', body: { isChecked, expiryDate: item.expiry_date }
    });
    loadChecklist();
  };
  const setChecklistExpiry = async (item, expiryDate) => {
    await api(`/dispatch/drivers/${savedId}/checklist/${item.doc_type_id}`, {
      method: 'POST', body: { isChecked: !!item.is_checked, expiryDate }
    });
    loadChecklist();
  };
  const addDocType = async () => {
    const name = window.prompt('New document type name');
    if (!name || !name.trim()) return;
    await api('/dispatch/driver-doc-types', { method: 'POST', body: { name, isRequired: false } });
    loadChecklist();
  };

  // ── Driver Rates (tab 3) ────────────────────────────────────────────────
  const [rates, setRates] = useState(null);
  const loadRates = () => {
    if (!savedId) return;
    api(`/dispatch/drivers/${savedId}/rates`).then(setRates).catch(() => setRates([]));
  };
  useEffect(() => { if (tab === 3 && savedId) loadRates(); }, [tab, savedId]);
  const [rateForm, setRateForm] = useState({ effectiveDate: '', rateType: '', rateValue: '' });
  const addRate = async () => {
    if (!rateForm.effectiveDate) return;
    await api(`/dispatch/drivers/${savedId}/rates`, { method: 'POST', body: rateForm });
    setRateForm({ effectiveDate: '', rateType: '', rateValue: '' });
    loadRates();
  };
  const deleteRate = async (id) => {
    await api(`/dispatch/drivers/${savedId}/rates/${id}`, { method: 'DELETE' });
    loadRates();
  };

  // ── Related Documents (tab 4) — client-preview only, no upload backend yet
  const [relatedDocs, setRelatedDocs] = useState([]);
  const handleAddDocs = (e) => {
    const files = Array.from(e.target.files || []);
    setRelatedDocs(list => [...list, ...files.map(f => f.name)]);
  };

  // ── Safety Records (tab 6) ──────────────────────────────────────────────
  const [safetyRecords, setSafetyRecords] = useState(null);
  const loadSafety = () => {
    if (!savedId) return;
    api(`/dispatch/drivers/${savedId}/safety-records`).then(setSafetyRecords).catch(() => setSafetyRecords([]));
  };
  useEffect(() => { if (tab === 6 && savedId) loadSafety(); }, [tab, savedId]);
  const addSafetyRecord = async ({ date, description }) => {
    await api(`/dispatch/drivers/${savedId}/safety-records`, { method: 'POST', body: { recordDate: date, description } });
    loadSafety();
  };
  const deleteSafetyRecord = async (id) => {
    await api(`/dispatch/drivers/${savedId}/safety-records/${id}`, { method: 'DELETE' });
    loadSafety();
  };

  const handlePhotoPick = (e) => {
    const file = e.target.files?.[0];
    if (file) setPhotoPreview(URL.createObjectURL(file));
  };

  const persist = async () => {
    if (!form.firstName.trim() && !form.lastName.trim()) { setError('First or last name is required.'); return null; }
    setSaving(true);
    setError(null);
    try {
      const saved = savedId
        ? await api(`/dispatch/drivers/${savedId}`, { method: 'PATCH', body: form })
        : await api('/dispatch/drivers', { method: 'POST', body: form });
      setSavedId(saved.id);
      return saved;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAndClose = async () => { if (await persist()) onSaved(); };
  const saveAndNext = async () => {
    if (!(await persist())) return;
    setTab(t => Math.min(t + 1, TABS.length - 1));
  };
  const reset = () => setForm(driver ? fromDriver(driver) : blank);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal customer-modal" onClick={e => e.stopPropagation()}>
        <div className="customer-modal-title">{driver ? 'Edit driver' : 'New driver'}</div>

        <div className="customer-modal-tabs">
          <div className="form-tabs" style={{ marginBottom: 0 }}>
            {TABS.map((t, i) => (
              <button key={t} type="button" className={i === tab ? 'form-tab active' : 'form-tab'} onClick={() => setTab(i)}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="customer-modal-body">
          {tab === 0 && (
            <>
              <label>Photo</label>
              <input id="driver-photo-input" type="file" accept="image/*" onChange={handlePhotoPick} style={{ display: 'none' }} />
              <label htmlFor="driver-photo-input" className="logo-upload" style={{ marginBottom: 18 }}>
                {photoPreview ? <img src={photoPreview} alt="Photo preview" /> : 'Click to upload an image'}
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
                <label>First Name<br/>
                  <input value={form.firstName} onChange={e => set('firstName', e.target.value)} />
                </label>
                <label>Last Name<br/>
                  <input value={form.lastName} onChange={e => set('lastName', e.target.value)} />
                </label>
                <label>Date of Birth<br/>
                  <input type="date" value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} />
                </label>
                <label>Place of Birth<br/>
                  <input value={form.placeOfBirth} onChange={e => set('placeOfBirth', e.target.value)} />
                </label>
                <label>Citizenship<br/>
                  <input value={form.citizenship} onChange={e => set('citizenship', e.target.value)} />
                </label>
              </div>

              <label>Address<br/>
                <AddressAutocomplete value={form.address} onChange={val => set('address', val)}
                  onPlaceSelected={({ address, city, province, postalCode, country }) => {
                    setForm(f => ({ ...f, address, city, province, postalCode, country }));
                    setManualAddress(true);
                  }} />
              </label>
              <button type="button" className="link-toggle" style={{ marginBottom: manualAddress ? 12 : 18 }}
                onClick={() => setManualAddress(m => !m)}>
                {manualAddress ? 'Hide manual fields' : "Can't find it? Enter manually"}
              </button>
              {manualAddress && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                  <label>City<br/><input value={form.city} onChange={e => set('city', e.target.value)} /></label>
                  <label>Province / State<br/><input value={form.province} onChange={e => set('province', e.target.value)} /></label>
                  <label>Postal / Zip<br/><input value={form.postalCode} onChange={e => set('postalCode', e.target.value)} /></label>
                  <label>Country<br/>
                    <select value={form.country} onChange={e => set('country', e.target.value)}>
                      <option>Canada</option><option>United States</option>
                    </select>
                  </label>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Email<br/><input value={form.email} onChange={e => set('email', e.target.value)} /></label>
                <label>Phone<br/><PhoneInput value={form.phone} onChange={v => set('phone', v)} /></label>
                <label>Cell<br/><PhoneInput value={form.cell} onChange={v => set('cell', v)} /></label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Emergency Contact<br/><input value={form.emergencyContact} onChange={e => set('emergencyContact', e.target.value)} /></label>
                <label>Emergency Contact Phone<br/><PhoneInput value={form.emergencyContactPhone} onChange={v => set('emergencyContactPhone', v)} /></label>
                <label>Dispatch Status (Active / Inactive)<br/>
                  <select value={form.dispatchStatus} onChange={e => set('dispatchStatus', e.target.value)}>
                    <option>Active</option><option>Inactive</option>
                  </select>
                </label>
              </div>

              <label>Status</label>
              <div style={{ display: 'flex', gap: 20, marginBottom: 18, marginTop: 8 }}>
                {['Active', 'Archive'].map(s => (
                  <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500 }}>
                    <input type="radio" name="driver-status" checked={form.driverStatus === s}
                      onChange={() => set('driverStatus', s)} style={{ width: 'auto' }} />
                    {s}
                  </label>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Date of Hiring<br/>
                  <input type="date" value={form.dateOfHiring} onChange={e => set('dateOfHiring', e.target.value)} />
                </label>
                <label>Time with us<br/>
                  <input value={timeWithUs(form.dateOfHiring)} disabled readOnly />
                </label>
              </div>

              <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text-dim)', marginBottom: 12 }}>DISPATCH DETAILS</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
                <label>Operational status<br/>
                  <select value={form.operationalStatus} onChange={e => set('operationalStatus', e.target.value)}>
                    <option value="">—</option><option>On Duty</option><option>Off Duty</option><option>On Leave</option>
                  </select>
                </label>
                <label>Driver Type<br/>
                  <select value={form.driverType} onChange={e => set('driverType', e.target.value)}>
                    <option value="">—</option><option>Company Driver</option><option>Owner Operator</option><option>Lease Operator</option>
                  </select>
                </label>
                <label>Job Status<br/>
                  <select value={form.jobStatus} onChange={e => set('jobStatus', e.target.value)}>
                    <option value="">—</option><option>Full-Time</option><option>Part-Time</option><option>Seasonal</option>
                  </select>
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <label>Division<br/>
                  <select value={form.division} onChange={e => set('division', e.target.value)}>
                    <option value="">—</option><option>Dry Van</option><option>Flatbed</option><option>Reefer</option>
                  </select>
                </label>
                <label>Driver Pay Option<br/>
                  <select value={form.driverPayOption} onChange={e => set('driverPayOption', e.target.value)}>
                    <option value="">—</option><option>Per Mile</option><option>Percentage</option><option>Salary</option><option>Hourly</option>
                  </select>
                </label>
              </div>

              <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text-dim)', margin: '18px 0 12px' }}>MOBILE APP LOGIN</div>
              {!savedId ? (
                <div className="empty">Save the driver first to create their mobile app login.</div>
              ) : loginInfo ? (
                <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '12px 14px', fontSize: 13 }}>
                  <div>Login created — share these with the driver (shown once, not stored):</div>
                  <div style={{ marginTop: 6 }}><strong>Email:</strong> {loginInfo.email}</div>
                  <div><strong>Temporary password:</strong> {loginInfo.tempPassword}</div>
                </div>
              ) : existingLogin === undefined ? (
                <div className="empty">Loading...</div>
              ) : existingLogin ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '12px 14px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13 }}><strong>Email:</strong> {existingLogin.email}</div>
                    <div style={{ fontSize: 12, color: existingLogin.is_active ? 'var(--green)' : 'var(--red)', fontWeight: 600, marginTop: 2 }}>
                      {existingLogin.is_active ? 'Active — driver can log in' : 'Disabled — driver cannot log in'}
                    </div>
                  </div>
                  <button type="button" onClick={toggleLogin} disabled={togglingLogin}
                    style={existingLogin.is_active ? { color: 'var(--red)' } : {}}>
                    {togglingLogin ? 'Working...' : existingLogin.is_active ? 'Disable Login' : 'Re-enable Login'}
                  </button>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10 }}>
                  <input value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="Login email for the driver app" />
                  <button type="button" className="primary-btn" onClick={createLogin} disabled={creatingLogin}>
                    {creatingLogin ? 'Creating...' : '+ Create Login'}
                  </button>
                </div>
              )}
              {loginError && <p style={{ color: '#c4364b', fontSize: 13, margin: '8px 0 0' }}>{loginError}</p>}
            </>
          )}

          {tab === 1 && (
            !savedId
              ? <div className="empty">Save the driver first to log documents.</div>
              : <DriverDocumentsTab driverId={savedId} />
          )}

          {tab === 2 && (
            !savedId ? (
              <div className="empty">Save the driver first to track document checklist items.</div>
            ) : checklist === null ? (
              <div className="empty">Loading...</div>
            ) : (
              <>
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 14 }}>
                  Check a document once it's in the system and set its expiry.
                </div>
                {checklist.length === 0 && <div className="empty">No document types yet.</div>}
                {checklist.map(item => (
                  <div className="checklist-row" key={item.doc_type_id}>
                    <input type="checkbox" checked={!!item.is_checked} style={{ width: 'auto' }}
                      onChange={e => toggleChecklistItem(item, e.target.checked)} />
                    <div style={{ flex: 1 }}>{item.name}</div>
                    {!!item.is_required && <span className="checklist-req-badge">Required</span>}
                    <span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 700 }}>EXPIRY</span>
                    <input type="date" value={item.expiry_date || ''} style={{ width: 150 }}
                      onChange={e => setChecklistExpiry(item, e.target.value)} />
                  </div>
                ))}
                <button type="button" onClick={addDocType} style={{ marginTop: 14 }}>+ Add document type</button>
              </>
            )
          )}

          {tab === 3 && (
            !savedId ? (
              <div className="empty">Save the driver first, then set pay rates here (rates are versioned per effective date).</div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 140px auto', gap: 10, marginBottom: 14 }}>
                  <input type="date" value={rateForm.effectiveDate} onChange={e => setRateForm(f => ({ ...f, effectiveDate: e.target.value }))} />
                  <select value={rateForm.rateType} onChange={e => setRateForm(f => ({ ...f, rateType: e.target.value }))}>
                    <option value="">Rate type</option>
                    <option>Per Mile</option><option>Percentage</option><option>Salary</option><option>Hourly</option>
                  </select>
                  <input type="number" placeholder="Value" value={rateForm.rateValue} onChange={e => setRateForm(f => ({ ...f, rateValue: e.target.value }))} />
                  <button type="button" className="primary-btn" onClick={addRate}>+ Add</button>
                </div>
                {(rates || []).length === 0 ? <div className="empty">No rates set yet.</div> : (
                  <table>
                    <thead><tr><th>Effective Date</th><th>Rate Type</th><th>Value</th><th></th></tr></thead>
                    <tbody>
                      {(rates || []).map(r => (
                        <tr key={r.id}>
                          <td>{r.effective_date}</td>
                          <td>{r.rate_type || '—'}</td>
                          <td>{r.rate_value != null ? Number(r.rate_value).toFixed(2) : '—'}</td>
                          <td><button type="button" onClick={() => deleteRate(r.id)} style={{ color: 'var(--red)' }}>Delete</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )
          )}

          {tab === 4 && (
            <>
              <input id="driver-docs-input" type="file" multiple onChange={handleAddDocs} style={{ display: 'none' }} />
              <label htmlFor="driver-docs-input" className="primary-btn" style={{ display: 'inline-block', marginBottom: 14, cursor: 'pointer' }}>
                Add New Documents +
              </label>
              {relatedDocs.length === 0 ? (
                <div className="empty">No documents uploaded yet.</div>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {relatedDocs.map((name, i) => <li key={i} style={{ fontSize: 13, marginBottom: 4 }}>{name}</li>)}
                </ul>
              )}
            </>
          )}

          {tab === 5 && (
            !savedId
              ? <div className="empty">No loads assigned to this driver yet.</div>
              : <RelatedLoadsTab endpoint={`/dispatch/drivers/${savedId}/loads`} />
          )}

          {tab === 6 && (
            !savedId ? (
              <div className="empty">Save this driver first to add safety records.</div>
            ) : (
              <EntryLog
                entries={safetyRecords || []}
                onAdd={addSafetyRecord}
                onDelete={deleteSafetyRecord}
              />
            )
          )}

          {error && <p style={{ color: '#c4364b', fontSize: 13, margin: '16px 0 0' }}>{error}</p>}
        </div>

        <div className="customer-modal-footer">
          <div className="modal-actions" style={{ margin: 0 }}>
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="button" onClick={reset}>Reset</button>
            <button type="button" className="primary-btn" onClick={saveAndNext} disabled={saving || tab === TABS.length - 1}>Save &amp; Next</button>
            <button type="button" className="primary-btn" onClick={saveAndClose} disabled={saving}>
              {saving ? 'Saving...' : 'Save & Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
